import { useCallback, useEffect, useState } from 'react';
import { NewDatePicker } from '../../PIM/common';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';

interface DateInputCellExtras {
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
  popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'auto';
}

type DateInputCellProps = BombiixCellRendererParams<any, string | Date | null> & DateInputCellExtras;

export const PORTAL_ID = 'bombiix-aggrid-datepicker-portal';

const toDate = (raw: string | Date | null | undefined): Date | null => {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDay = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Date cell backed by the PIM NewDatePicker. Row value is stored as an ISO day
 * string (`YYYY-MM-DD`); consumers that prefer a `Date` object can normalise in
 * their onRowChange handler. The popover portals to a body-level container so
 * it isn't clipped by AG Grid's cell overflow.
 */
function DateInputCell(params: DateInputCellProps) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, disabled, error, dateFormat, minDate, maxDate, filterDate, popperPlacement } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;

  const [local, setLocal] = useState<Date | null>(() => toDate(value));

  useEffect(() => {
    setLocal(toDate(value));
  }, [value]);

  const handleChange = useCallback(
    (next: Date | null) => {
      setLocal(next);
      if (rowId !== undefined && field) onRowChange?.(rowId, field, toIsoDay(next));
    },
    [rowId, field, onRowChange],
  );

  const datePickerProps: Record<string, unknown> = {
    value: local,
    onChange: handleChange,
    placeholder: placeholder ?? colDef?.headerName ?? 'Date',
    disabled: Boolean(disabled),
    invalid: Boolean(error),
    portalId: PORTAL_ID,
  };
  if (dateFormat) datePickerProps.dateFormat = dateFormat;
  if (minDate) datePickerProps.minDate = minDate;
  if (maxDate) datePickerProps.maxDate = maxDate;
  if (filterDate) datePickerProps.filterDate = filterDate;
  if (popperPlacement) datePickerProps.popperPlacement = popperPlacement;

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <NewDatePicker {...(datePickerProps as unknown as React.ComponentProps<typeof NewDatePicker>)} />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default DateInputCell;
